import { useTenantQuery } from "@/shared/services/graphql/generated/consumer-graph-types";
import { ref } from "vue";
import { TenantAuthConfig } from "@/shared/services/graphql/generated/public-graph-types";
import { LOCAL_STORAGE_KEY, useLanguageStore } from "@/shared/store/language-store";
import { resolveInitialLocale } from "@/shared/utils/language-utils.ts";

export interface Tenant {
    defaultLanguage: string;
    availableLanguages: string[];
    authConfig: TenantAuthConfig;
    displayName: string;
    landingPage?: string;
    docsRouteParams?: Record<string, string>;
}

const tenant = ref<Tenant | undefined>(undefined);

export const useTenant = () => {
    const { error, loading, onResult } = useTenantQuery({ clientId: "public", fetchPolicy: "cache-first" });

    function initLanguage(defaultLng: string, available: string[]) {
        const store = useLanguageStore();
        store.loadFromLocalStorage();

        const locale = resolveInitialLocale({
            persistedLocale: localStorage.getItem(LOCAL_STORAGE_KEY) ? store.languageSettings.uiLocale : undefined,
            defaultLanguage: defaultLng,
            availableLanguages: available,
        });

        store.setLanguages({
            uiLocale: locale,
            databaseLocale: locale,
            fallbackLocales: defaultLng ? [defaultLng] : store.fallbackLocales,
            availableLocales: available ?? store.availableLocales,
        });
    }

    onResult(({ data }) => {
        if (!data || tenant.value) return;

        initLanguage(data.tenant.languages?.default ?? "", (data.tenant.languages?.available as string[]) || []);

        tenant.value = {
            defaultLanguage: data.tenant.languages?.default ?? "",
            availableLanguages: (data.tenant.languages?.available as string[]) || [],
            authConfig: data.tenant.auth as TenantAuthConfig,
            displayName: data.tenant.displayName,
            landingPage: data.tenant.landingPage?.startsWith("/") ? data.tenant.landingPage : undefined,
            docsRouteParams: data.tenant.tenantSettings?.docsRouteParams,
        };
    });

    return { tenant, error, loading };
};
